import { DocumentParseResponse } from '@business/entities/parse';
import { CustomFieldResponseType } from './../integration/IntegrationResponse';
import { UserId } from '@business/entities/account';
import { BrandId } from '@business/entities/brand';
import { CompactDealResponse, HasDeal } from '@business/entities/deal';
import { PaymentMethodResponse } from '@business/entities/organization';
import { SubscriptionId } from '@business/entities/project/SubscriptionResponse';
import { SalesStatusType, TransactionInvoiceResponse } from '@business/entities/TransactionInvoice';
import { CompactUserResponse } from '../account';
import { BrandResponse } from '../brand';
import { ContactOrgRequest, ContactResponse, CompactContactResponse } from '../contact';
import { CompactProjectResponse } from '../project';
import { SubscriptionResponse } from '../project/SubscriptionResponse';
import { InvoiceId } from '../TransactionInvoice';
import { PageQuery } from './../common/PageQuery';
import { SalesInvoiceType } from './../TransactionInvoice/TransactionInvoiceResponse';
import { InvoiceSettingsResponse } from './InvoiceSettingsResponse';
import { OrderTermId, PricingTierId } from '../order';
import { OrganizationStatusResponse, OrgStatusId } from '../organization_status';
import { EstimateLineItem, CompletedLineResponse } from '../tm';
import { StopTimeTrackingFormData } from '@modules/common/TimeLog/StopTimeTrackingModal/StopTimeTrackingModal';
import { TmInvoiceSalesTaxData } from '@modules/common/TmInvoiceSalesTax';

export interface OrgPageQuery extends PageQuery {
  ownerId?: UserId;
  status?: OrgStatusId;
  hasDeal?: HasDeal;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
  brand?: BrandId;
}

export interface OrgResponseAdditional {
  requestId: number;
}

export enum OrgAddressType {
  billing = 'billing',
  shipping = 'shipping',
  both = 'both',
}

export interface OrgAddressExtraResponse {
  dialCode?: string;
  countryCode?: string;
}

export interface OrgAddressResponse {
  id: string;
  address: string;
  addressLineTwo: string | null;
  city: string;
  state: string;
  postal: string;
  country: string;
  isDefault: boolean;
  type: OrgAddressType;
  name: string;
  phone: string;
  notes: string;
  contact?: CompactContactResponse | null;
  extra?: OrgAddressExtraResponse;
}

export type JustAddressReponse = Pick<OrgAddressResponse, 'address' | 'addressLineTwo' | 'city' | 'country' | 'postal' | 'state'>;
export enum TransactionType {
  project = 'project',
  subscription = 'subscription',
  oneTime = 'one-time',
}

export interface TransactionResponse {
  id: number;
  title: string;
  amount: number;
  type: TransactionType;
  invoiceId: number;
  paymentTime: number;
  method: string;
  chargeId: string;
  status: SalesStatusType;
  invoiceType: SalesInvoiceType;
  showRefund?: boolean;
  errorMessage?: string | null;
}

export type OrgId = string;
export type OrgImportId = number;

export interface OrgExtra {
  hotness: number;
  reportUrl: string | null;
  lastActivityDetail?: string;
  lastActivityDateTime?: number;
  accountGroupId?: string;
}
export interface CanCreateResponse {
  id: number;
  canSee: boolean;
}

export interface UnapidInvoiceResponse {
  id: InvoiceId;
  subscriptionId: SubscriptionId;
  title: string;
  amount: number;
  balance: number;
  invoiceDate: number;
  dueDate: number;
  canProcess: boolean;
}

export enum OrgBillingType {
  self = 'self',
  parent = 'parent',
}
export interface OrgResponse {
  id: OrgId;
  name: string;
  isVip: boolean;
  status: OrganizationStatusResponse;
  brand: BrandResponse;
  importId: OrgImportId;
  owner: CompactUserResponse;
  user: CompactUserResponse;
  lastActivity: number;

  addresses: OrgAddressResponse[];

  contacts: ContactResponse[];
  methods: PaymentMethodResponse[];
  projects: CompactProjectResponse[];
  deals: CompactDealResponse[];
  transactions: TransactionResponse[];
  subscriptions: SubscriptionResponse[];

  customFields: { [key: string]: CustomFieldResponseType };

  websites: string[];
  extra: OrgExtra;
  createdAt: number;
  updatedAt: number;

  vendastaId?: string;
  vendastaName?: string;
  invoiceSettings: InvoiceSettingsResponse;
  termId?: OrderTermId;
  tierId?: PricingTierId;
  unpaidInvoices: UnapidInvoiceResponse[];
  invoices: TransactionInvoiceResponse[];
  tags: string[];

  parentId?: OrgId;
  parent?: CompactOrgResponse;
  billingType: OrgBillingType;
  children: CompactOrgResponse[];
  isTaxExempt: boolean;
  taxExemptReasonId: string;
  isImported: boolean;

  paymentMethodUrl: string;

  currency: string;
  companyExchangeRate: number;
  paymentId: string;
  importType: string;
  taxId: string;

  lifespan: number;
  pf: number;
  pv: number;
  lifetimeValue: number;
  totalRevenue: number;

  affiliates: string[];

  summary?: OrgSummary;

  accountManager?: CompactUserResponse | null;
}

export type OrgRequest = Pick<OrgResponse, 'name' | 'isVip' | 'status' | 'termId' | 'tierId' | 'parentId' | 'billingType' | 'taxId'> & {
  brand: BrandId;
  owner: UserId;
  isTaxExempt: boolean;
  taxExemptReasonId: string;
  isContact: boolean;
  customFields?: { [key: string]: CustomFieldResponseType };
  currency: string;
  parseResponse?: DocumentParseResponse | null;
} & Partial<ContactOrgRequest>;

export type CompactOrgResponse = Pick<
  OrgResponse,
  | 'id'
  | 'name'
  | 'websites'
  | 'status'
  | 'brand'
  | 'user'
  | 'owner'
  | 'lastActivity'
  | 'contacts'
  | 'importId'
  | 'customFields'
  | 'extra'
  | 'vendastaId'
  | 'vendastaName'
  | 'termId'
  | 'tierId'
  | 'parentId'
  | 'billingType'
  | 'isVip'
  | 'isTaxExempt'
  | 'taxExemptReasonId'
  | 'isImported'
  | 'paymentMethodUrl'
  | 'currency'
  | 'companyExchangeRate'
  | 'extra'
  | 'taxId'
  | 'summary'
  | 'accountManager'
>;

export type SimpleOrgResponse = Pick<
  OrgResponse,
  | 'id'
  | 'name'
  | 'websites'
  | 'status'
  | 'lastActivity'
  | 'importId'
  | 'extra'
  | 'vendastaId'
  | 'vendastaName'
  | 'termId'
  | 'tierId'
  | 'parentId'
  | 'billingType'
  | 'isVip'
>;

export type CompactOrgSmallResponse = Pick<OrgResponse, 'id' | 'name'>;

export type ManualSupportHoursRequest = {
  subId?: SubscriptionId;
  expiredAt: number;
  supportHours: number;
  notes: string;
};

export interface HourBreakupResponse {
  id: string;
  title: string;
  total: number;
  used: number;
  remaining: number;
  expiresAt: number;
}
export interface SupportHourInfo {
  supportTime: number;
  trackedTime: number;
  hoursBreakup: HourBreakupResponse[];
}

export interface PublicOrgResponse {
  id: OrgId;
  name: string;
  invoiceSettings: InvoiceSettingsResponse;
  processor: string;
  nmiToken: string;
  addresses?: OrgAddressResponse[];
}

export interface CollectJsResponse {
  card: CollectJsCardResponse;
  check: CollectJsCheckResponse;
  token: string;
}

export interface CollectJsCardResponse {
  bin: string;
  exp: string;
  hash: string;
  number: string;
  type: string;
}

export interface CollectJsFormResponse {
  card: CollectJsCardResponse;
  token: string;
  name: string;
  addressId?: string;
  address?: string;
  addressLineTwo?: string | null;
  city?: string;
  state?: string;
  postal?: string;
  country?: string;
}

export interface CollectJsCheckResponse {
  token: string;
  name: string;
  account: string;
  aba: string;
  hash: string;
}

export interface VeryCompactOrgResponse {
  id: OrgId;
  name: string;
  address: OrgAddressResponse;
  website?: string;
}

export interface TimeTrackingInfoResponse {
  customFields: Partial<StopTimeTrackingFormData>;
  items: CompletedLineResponse[];
}

export interface OrgSummary {
  summary: string;
  summaryAt: number;
}

export interface RefundLineResponse {
  id: string;
  amount: number;
}

export interface InvoiceLine {
  id: string;
  quantity: number;
  taxable: boolean;
  title?: string;
  rate: number;
  amount?: number;
}

export interface RefundFormResponse {
  invoiceLines: InvoiceLine[];
  amount: number;
  salesTax: TmInvoiceSalesTaxData;
}
